import PropTypes from 'prop-types';
import React, {
  useCallback,
  useState,
  useEffect,
} from 'react';

import EmailRecipientSelector from './EmailRecipientSelector';

const REPLY_TO_HELP = 'The form author\'s email address will be set as the reply to email';

export default function EmailRecipientSelectors({
  id,
  data,
  setDataMap,
  divisionId,
  divisionIds = [],
  additionalOptions,
  sections,
  isExternalForm,
  setDisableNext,
  setShowErrorPopover,
}) {
  const {
    users: initialSelected,
    cc: initialSelectedCC,
    bcc: initialSelectedBCC,
    replyTo: initialSelectedReplyTo,
  } = data ?? {};

  const [selectedRecipients, setSelectedRecipients] = useState(initialSelected);

  useEffect(() => {
    if (!setDisableNext && !setShowErrorPopover) return;

    const hasNoRecipients = !selectedRecipients?.length;
    setDisableNext((prev) => ({ ...prev, [id]: hasNoRecipients }));
    setShowErrorPopover(hasNoRecipients);
  }, [selectedRecipients, setDisableNext, setShowErrorPopover, id]);

  const onChange = useCallback((field) => (newSelected) => {
    if (field === 'users') {
      setSelectedRecipients(newSelected);
    }
    if (setDataMap) {
      setDataMap((dataMap) => {
        const {
          [id]: oldData = {},
        } = dataMap;
        return {
          ...dataMap,
          [id]: {
            ...oldData,
            [field]: newSelected,
          },
        };
      });
    }
  }, [id, setDataMap]);

  return (
    <>
      <EmailRecipientSelector
        sections={sections}
        initialSelected={initialSelected}
        divisionId={divisionId}
        divisionIds={divisionIds}
        onChange={onChange('users')}
        additionalOptions={additionalOptions}
        text="Select Recipient(s):"
        showFormAuthor={!isExternalForm}
      />
      <EmailRecipientSelector
        sections={sections}
        initialSelected={initialSelectedCC}
        divisionId={divisionId}
        divisionIds={divisionIds}
        onChange={onChange('cc')}
        additionalOptions={additionalOptions}
        text="Select CC Recipient(s):"
        showFormAuthor={!isExternalForm}
      />
      <EmailRecipientSelector
        sections={sections}
        initialSelected={initialSelectedBCC}
        divisionId={divisionId}
        divisionIds={divisionIds}
        onChange={onChange('bcc')}
        additionalOptions={additionalOptions}
        text="Select BCC Recipient(s):"
        showFormAuthor={!isExternalForm}
      />
      <EmailRecipientSelector
        sections={sections}
        initialSelected={initialSelectedReplyTo}
        divisionId={divisionId}
        divisionIds={divisionIds}
        onChange={onChange('replyTo')}
        additionalOptions={additionalOptions}
        text="Select Reply-To Address(es):"
        showFormAuthor={!isExternalForm}
        authorHelpText={REPLY_TO_HELP}
      />
    </>
  );
}

EmailRecipientSelectors.propTypes = {
  additionalOptions: PropTypes.array,
  data: PropTypes.shape({}).isRequired,
  divisionId: PropTypes.string,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string.isRequired,
  isExternalForm: PropTypes.bool,
  sections: PropTypes.array,
  setDataMap: PropTypes.func.isRequired,
  setDisableNext: PropTypes.func,
  setShowErrorPopover: PropTypes.func,
};

EmailRecipientSelectors.defaultProps = {
  additionalOptions: [],
  divisionId: null,
  divisionIds: [],
  isExternalForm: false,
  sections: [],
  setDisableNext: null,
  setShowErrorPopover: null,
};
